import { localizeApiMessage } from "./errors";

export interface ApiEnvelope<T> {
  code: number;
  message?: string;
  data: T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const readToken = () => localStorage.getItem("AUTH-TOKEN") || "";

const handleExpiredSession = (httpStatus: number, apiCode?: number) => {
  if (!readToken() || (httpStatus !== 401 && httpStatus !== 403 && apiCode !== 403))
    return;

  localStorage.removeItem("AUTH-TOKEN");
  localStorage.removeItem("GOP ALA-USER");
  if (!window.location.pathname.startsWith("/auth/"))
    window.location.replace("/auth/login");
};

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

const resolveApiUrl = (path: string) => {
  if (!apiBaseUrl || /^https?:\/\//i.test(path)) return path;
  return `${apiBaseUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(resolveApiUrl(path), {
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Accept-Language": `${document.documentElement.lang || "es-EC"},en;q=0.7`,
      ...(readToken() ? { "AUTH-TOKEN": readToken() } : {}),
      ...init.headers,
    },
  });

  let payload: ApiEnvelope<T> | undefined;
  try {
    payload = (await response.json()) as ApiEnvelope<T>;
  } catch {
    payload = undefined;
  }
  handleExpiredSession(response.status, payload?.code);
  if (!response.ok)
    throw new ApiError(
      localizeApiMessage(payload?.message, response.status),
      response.status,
    );
  if (!payload)
    throw new ApiError(localizeApiMessage(undefined, response.status));
  if (payload.code !== 200)
    throw new ApiError(localizeApiMessage(payload.message));
  return payload.data;
}

export async function upload<T>(path: string, body: FormData): Promise<T> {
  const response = await fetch(resolveApiUrl(path), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Accept-Language":
        (document.documentElement.lang || "es-EC") + ",en;q=0.7",
      ...(readToken() ? { "AUTH-TOKEN": readToken() } : {}),
    },
    body,
  });

  let payload: ApiEnvelope<T> | undefined;
  try {
    payload = (await response.json()) as ApiEnvelope<T>;
  } catch {
    payload = undefined;
  }
  handleExpiredSession(response.status, payload?.code);
  if (!response.ok)
    throw new ApiError(
      localizeApiMessage(payload?.message, response.status),
      response.status,
    );
  if (!payload)
    throw new ApiError(localizeApiMessage(undefined, response.status));
  if (payload.code !== 200)
    throw new ApiError(localizeApiMessage(payload.message));
  return payload.data;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
};
